/**
 * Product Board — roadmap dashboard (NOW / NEXT / LATER, Status Board)
 * Sidebar: /product-board · Dev: ?test=product-management
 */

import { testIds } from '@/lib/test-ids';
import { Home, Map, Target, Clock, Calendar, CheckCircle2, Circle, ExternalLink, FileText, X, LayoutGrid, List, ChevronDown, ChevronRight, Plus, Trash2, MoveUp, MoveDown, Search, Undo2, Redo2, ClipboardList, Pencil, RefreshCw, MoreHorizontal, Copy, Share2, MessageSquarePlus, GripVertical, MoveLeft, MoveRight, PanelRight, Loader2, ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Toaster } from '@/components/ui/toaster';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { Input } from '@/components/ui/input';
import { PanelSearchBar } from '@/components/ui/panel-search-bar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { RoadmapItem } from './product-board-types';
import { sectionColors, ALL_SECTION_COLORS } from './product-board-types';
import { normalizeBetDocFilePath, betDocumentReferenceId, getRawBetFilePath, getBetFileName, buildMcpPathSnippet, buildDelegatePrompt, isDispatchReady, isShapingNeeded, isDispatchBlocked, splitBoardTagFiltersInlineVsOverflow } from './product-board.utils';
import { resolveCloudDispatchTargets } from './bet-handoff-path';
import { ProductBoardMarkdownPreview, RoadmapLoadingSkeleton, SortableBetCard, DroppableSection } from './product-board.components';
import { useProductBoardState } from './product-board.hooks';
import { PreviewPaneHeader } from '@/components/features/preview-pane-header';
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle';
import { ListSurfaceStatusBar } from '@/components/ui/list-surface-status-bar';
import { LIST_GESTURE_TIPS_PRODUCT_BOARD } from '@/lib/list-gesture-tips';
import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BetPreviewCloudDispatchSection } from './bet-preview-cloud-dispatch';
import { AgentLiveStreamPanel } from '@/components/features/agent/agent-live-stream-panel';
import { isActiveCloudDispatch } from '@/lib/cloud-dispatch';
import { ProductBoardDashboard } from './product-board.dashboard';
import { ProductBoardCycleDetail } from './product-board.cycle-detail';

export type ProductBoardProps = {
  /** Load board via `GET /api/roadmap?documentId=` (UUID). */
  documentId?: string;
  /** Load board via `GET /api/roadmap?filePath=` when tab id is a file path. */
  documentFilePath?: string;
  /**
   * `embedded` is the document renderer (DocumentContent supplies Preview/Code).
   * `page` is the standalone / legacy tab (own status bar, no source toggle).
   */
  chrome?: 'page' | 'embedded';
};

const VIEW_TAB_CLASS = (active: boolean) =>
  cn(
    'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
    active
      ? 'bg-background text-foreground shadow-sm'
      : 'text-muted-foreground hover:text-foreground',
  );

export function ProductBoard({ documentId, documentFilePath, chrome = 'page' }: ProductBoardProps = {}) {
  const [watchLiveAgentId, setWatchLiveAgentId] = useState<string | null>(null);
  const state = useProductBoardState({ documentId, documentFilePath });
  
  const {
    delegateMcpOrganizationId,
    currentView,
    setCurrentView,
    searchOpen,
    searchQuery,
    setSearchQuery,
    inputRef,
    close: closeSearch,
    open: openSearch,
    betSearchDim,
    isSearchActive,
    displaySections,
    isBoardFilterActive,
    boardTagFilterEntries,
    boardCycleSelectRows,
    boardFilterMvpCount,
    boardFilterDispatchCount,
    boardFiltersState,
    filtersExpanded,
    setFiltersExpanded,
    toggleBoardFilterMvp,
    toggleBoardFilterDispatch,
    toggleBoardFilterTag,
    setBoardFilterCycle,
    clearBoardFilters,
    selectedBetId,
    sidePanelView,
    sidePanelBetId,
    sidePanelRoadmapPath,
    betPreviewMarkdown,
    betPreviewLoading,
    betPreviewError,
    cloudDispatchingId,
    cloudDispatchResult,
    syncCloudDispatchForItem,
    roadmapViewMode,
    setRoadmapViewMode,
    collapsedSections,
    sections,
    editingSectionId,
    setEditingSectionId,
    editingSectionTitle,
    setEditingSectionTitle,
    roadmapLoading,
    roadmapDataReady,
    canUndo,
    canRedo,
    animatingIds,
    overId,
    activeDragItem,
    lastBoardUpdate,
    setLastBoardUpdate,
    setSidePanelBetId,
    setSidePanelView,
    blockedItems,
    sessionNotes,
    showNoteInput,
    setShowNoteInput,
    noteInputText,
    setNoteInputText,
    endOfSessionChecks,
    setEndOfSessionChecks,
    loadRoadmapData,
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    undo,
    redo,
    addItemSearch,
    setAddItemSearch,
    addItemSectionId,
    setAddItemSectionId,
    filteredSearchResults,
    addItemToSection,
    createNewItem,
    toggleSection,
    addSection,
    deleteSection,
    renameSection,
    changeColumnColor,
    moveSectionLeft,
    moveSectionRight,
    moveItemToSection,
    promoteFeedbackTicket,
    updateItemStatusBoard,
    addBlockedItem,
    updateBlockedItem,
    removeBlockedItem,
    addSessionNote,
    archiveItem,
    completedTasks,
    handleTaskToggle,
    handleOpenBetInPanel,
    handleOpenRoadmapColumnDocInPanel,
    handleDispatchToCloud,
    handleCloseSidePanel,
    handleToggleSidePanel,
    handleOpenInEditor,
  } = state;

  const MAX_INLINE_BOARD_TAG_FILTERS = 10;
  const { inline: inlineBoardTagFilters, overflow: overflowBoardTagFilters } = useMemo(
    () =>
      splitBoardTagFiltersInlineVsOverflow(
        boardTagFilterEntries,
        boardFiltersState.tagConjunction,
        MAX_INLINE_BOARD_TAG_FILTERS
      ),
    [boardTagFilterEntries, boardFiltersState.tagConjunction]
  );

  const activeFilterCount =
    (boardFiltersState.mvpOnly ? 1 : 0) +
    (boardFiltersState.dispatchOnly ? 1 : 0) +
    boardFiltersState.tagConjunction.length +
    (boardFiltersState.cycle !== 'all' ? 1 : 0);

  const renderDashboard = () => (
    <ProductBoardDashboard
      sections={sections}
      blockedItems={blockedItems}
      lastBoardUpdate={lastBoardUpdate}
      roadmapDataReady={roadmapDataReady}
      roadmapLoading={roadmapLoading}
      documentFilePath={documentFilePath}
      delegateMcpOrganizationId={delegateMcpOrganizationId}
      setCurrentView={setCurrentView}
      loadRoadmapData={loadRoadmapData}
      setSidePanelBetId={setSidePanelBetId}
      setSidePanelView={setSidePanelView}
    />
  );

  const renderRoadmap = () => {
    // Rows View (Asana-style vertical collapsible groups) - Now using dynamic sections
    const renderRowsView = () => (
      <div className="space-y-4">
        {displaySections.map((section, index) => {
          const colors = sectionColors[section.color];
          const isEditing = editingSectionId === section.id;
          const isFirst = index === 0;
          const isLast = index === sections.length - 1;
          const isAnimating = animatingIds.has(section.id);
          
          return (
            <div key={section.id} className={`border border-border rounded-lg bg-card overflow-hidden group/section transition-all duration-300 ${isAnimating ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : ''}`}>
              {/* Section Header */}
              <div className="px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  {/* Collapse toggle */}
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="p-0.5 hover:bg-muted rounded"
                  >
                    {collapsedSections.has(section.id) ? (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                  
                  {/* Editable title */}
                  {isEditing ? (
                    <input
                      type="text"
                      value={editingSectionTitle}
                      onChange={(e) => setEditingSectionTitle(e.target.value)}
                      onBlur={() => renameSection(section.id, editingSectionTitle)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameSection(section.id, editingSectionTitle);
                        if (e.key === 'Escape') {
                          setEditingSectionId(null);
                          setEditingSectionTitle('');
                        }
                      }}
                      className="text-lg font-semibold bg-transparent border-b border-primary outline-none px-1"
                      autoFocus
                    />
                  ) : (
                    <h2
                      className="text-lg font-semibold cursor-pointer hover:text-primary transition-colors"
                      onClick={() => {
                        setEditingSectionId(section.id);
                        setEditingSectionTitle(section.title);
                      }}
                      title="Click to rename"
                    >
                      {section.title}
                    </h2>
                  )}
                  
                  {/* Badge */}
                  {section.badge && (
                    <span className={`px-2 py-0.5 text-xs rounded ${colors.bg} ${colors.text} border ${colors.border}`}>
                      {section.badge}
                    </span>
                  )}
                  {section.sourceFilePath && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                      title={`Open ${section.title}.md in the side preview panel`}
                      data-testid={testIds.productBoard.roadmapColumnOpenPreview(section.id)}
                      onClick={() => handleOpenRoadmapColumnDocInPanel(section.id)}
                    >
                      <PanelRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                {/* Row controls — ... menu on hover */}
                <div className="flex items-center opacity-0 group-hover/section:opacity-100 transition-opacity">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                      collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                    >
                      <DropdownMenuItem onClick={() => { setEditingSectionId(section.id); setEditingSectionTitle(section.title); }} className="gap-2">
                        <Pencil className="w-4 h-4" /> Rename
                      </DropdownMenuItem>
                      {/* Color palette */}
                      <div className="px-2 py-1.5">
                        <p className="text-xs text-muted-foreground mb-1.5">Color</p>
                        <div className="flex gap-1.5">
                          {ALL_SECTION_COLORS.map(c => (
                            <button key={c} onClick={() => changeColumnColor(section.id, c)}
                              className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${sectionColors[c].dotBg} ${section.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                              title={c}
                            />
                          ))}
                        </div>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => moveSectionLeft(section.id)} disabled={isFirst} className="gap-2">
                        <MoveUp className="w-4 h-4" /> Move up
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => moveSectionRight(section.id)} disabled={isLast} className="gap-2">
                        <MoveDown className="w-4 h-4" /> Move down
                      </DropdownMenuItem>
                      {section.sourceFilePath && (<>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleOpenRoadmapColumnDocInPanel(section.id)} className="gap-2">
                          <PanelRight className="w-4 h-4" /> Open source doc
                        </DropdownMenuItem>
                      </>)}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setAddItemSectionId(addItemSectionId === section.id ? null : section.id)} className="gap-2">
                        <Plus className="w-4 h-4" /> Add item
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => deleteSection(section.id)} disabled={section.items.length > 0} className={cn('gap-2', QUIET_MENU_DESTRUCTIVE_ITEM_CLASS)}>
                        <Trash2 className="w-4 h-4" /> Delete row
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Item count */}
                <span className="text-sm text-muted-foreground ml-4">
                  {section.items.length} item{section.items.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Add item inline search (triggered from ... menu) */}
              {addItemSectionId === section.id && (
                <div className="mx-4 mb-2 bg-card border border-border rounded-lg shadow-lg z-50">
                  <div className="p-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="Search documents..." value={addItemSearch} onChange={(e) => setAddItemSearch(e.target.value)} className="pl-8" autoFocus />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredSearchResults.map((result) => (
                      <button key={result.id} onClick={() => addItemToSection(section.id, result)} className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-muted-foreground" /><span>{result.title}</span>
                        <span className="text-xs text-muted-foreground ml-auto">{result.type}</span>
                      </button>
                    ))}
                    {filteredSearchResults.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground text-center">No results found</div>}
                  </div>
                  <div className="border-t p-2 space-y-1">
                    <button onClick={() => createNewItem(section.id, 'bet')} className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm rounded">
                      <Plus className="w-4 h-4 text-green-500" /><span>Create new bet</span>
                    </button>
                    <button onClick={() => createNewItem(section.id, 'note')} className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm rounded">
                      <Plus className="w-4 h-4 text-blue-500" /><span>Create new note</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Section content */}
              {!collapsedSections.has(section.id) && (
                <div className="px-4 pb-4">
                  <SortableContext 
                    items={section.items.map(i => i.id)} 
                    strategy={verticalListSortingStrategy}
                  >
                    <DroppableSection 
                      section={section} 
                      isOver={overId === `section-drop-${section.id}` || section.items.some(i => i.id === overId)}
                    >
                      {section.items.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">
                          No items in this row
                        </div>
                      ) : (
                        section.items.map((item) => (
                          <SortableBetCard
                            key={item.id}
                            item={item}
                            sectionId={section.id}
                            isAnimating={animatingIds.has(item.id)}
                            onOpenInPanel={handleOpenBetInPanel}
                            otherSections={sections.filter(s => s.id !== section.id)}
                            onMoveToSection={moveItemToSection}
                            onArchive={archiveItem}
                            isInDoneColumn={section.id === 'done'}
                            delegateMcpOrganizationId={delegateMcpOrganizationId}
                            searchDimmed={betSearchDim(item)}
                            isActive={sidePanelBetId === item.id}
                            onDispatchToCloud={handleDispatchToCloud}
                            onPromoteFeedback={(ticket) => {
                              const path = window.prompt(
                                'promoted_to path (incident/bet file path):',
                                ''
                              );
                              if (path) void promoteFeedbackTicket(ticket, path);
                            }}
                            isDispatching={cloudDispatchingId === item.id}
                            isDispatched={
                              isActiveCloudDispatch(cloudDispatchResult[item.id]) ||
                              Boolean(item.dispatchedAgentId?.trim() && item.dispatchStatus === 'running')
                            }
                          />
                        ))
                      )}
                    </DroppableSection>
                  </SortableContext>
                  
                  {/* Add item placeholder - same as header button, opens dropdown */}
                  <button
                    onClick={() => setAddItemSectionId(addItemSectionId === section.id ? null : section.id)}
                    className="w-full py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-dashed border-border rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add item...
                  </button>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Add Row button */}
        <button
          onClick={addSection}
          className="w-full py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-dashed border-border rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Row
        </button>
      </div>
    );

    const isColumnsMode = roadmapViewMode === 'columns';
    return (
      <div className={isColumnsMode ? "flex flex-col h-full min-h-0" : "w-full min-w-0 space-y-6"}>
        <div
          className={cn(
            '@container flex min-w-0 shrink-0 items-center justify-between gap-2',
            isColumnsMode ? 'pt-3 pb-2' : 'pt-1',
          )}
        >
          <div className="min-w-0 text-xs text-muted-foreground truncate">
            {!roadmapDataReady ? (
              <span className="text-blue-500 animate-pulse">Loading roadmap from server...</span>
            ) : (
              <>
                {roadmapLoading && (
                  <span className="text-blue-500 animate-pulse">Refreshing...</span>
                )}
                {isBoardFilterActive && !roadmapLoading && (
                  <span>
                    Showing {displaySections.reduce((n, s) => n + s.items.length, 0)} of{' '}
                    {sections.reduce((n, s) => n + s.items.length, 0)} bets
                  </span>
                )}
              </>
            )}
          </div>

          <div className="flex min-w-0 shrink-0 items-center gap-2">
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={undo}
                disabled={!roadmapDataReady || !canUndo}
                className={`p-2 rounded-md transition-colors ${roadmapDataReady && canUndo ? 'hover:bg-muted text-foreground' : 'text-muted-foreground/30 cursor-not-allowed'}`}
                title="Undo (Cmd+Z)"
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                onClick={redo}
                disabled={!roadmapDataReady || !canRedo}
                className={`p-2 rounded-md transition-colors ${roadmapDataReady && canRedo ? 'hover:bg-muted text-foreground' : 'text-muted-foreground/30 cursor-not-allowed'}`}
                title="Redo (Cmd+Shift+Z)"
              >
                <Redo2 className="w-4 h-4" />
              </button>
            </div>

            <div className="flex shrink-0 items-center gap-0.5 bg-muted/60 rounded-lg p-0.5">
              <button
                className={VIEW_TAB_CLASS(roadmapViewMode === 'columns')}
                disabled={!roadmapDataReady}
                onClick={() => setRoadmapViewMode('columns')}
              >
                <LayoutGrid className="w-4 h-4" />
                Columns
              </button>
              <button
                className={VIEW_TAB_CLASS(roadmapViewMode === 'rows')}
                disabled={!roadmapDataReady}
                onClick={() => setRoadmapViewMode('rows')}
              >
                <List className="w-4 h-4" />
                Rows
              </button>
            </div>

            <button
              type="button"
              onClick={() => openSearch()}
              disabled={!roadmapDataReady}
              className={`p-2 rounded-md transition-colors ${
                roadmapDataReady ? 'hover:bg-muted text-foreground' : 'text-muted-foreground/30 cursor-not-allowed'
              }`}
              title="Search bets (Cmd+F)"
              data-testid={testIds.productBoard.roadmapFilterOpen}
            >
              <Search className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => setFiltersExpanded((open) => !open)}
              disabled={!roadmapDataReady}
              aria-expanded={filtersExpanded}
              aria-pressed={filtersExpanded}
              title={filtersExpanded ? 'Hide board filters' : 'Show board filters'}
              data-testid={testIds.productBoard.roadmapFiltersToggle}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm transition-colors',
                !roadmapDataReady
                  ? 'text-muted-foreground/30 cursor-not-allowed'
                  : filtersExpanded || isBoardFilterActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <ListFilter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="text-xs tabular-nums text-muted-foreground">({activeFilterCount})</span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className={isColumnsMode ? 'flex-shrink-0' : undefined}>
            <PanelSearchBar
              inputRef={inputRef}
              value={searchQuery}
              onChange={setSearchQuery}
              onClose={closeSearch}
              placeholder="filter bets..."
              resultCount={
                isSearchActive || isBoardFilterActive
                  ? displaySections.reduce((n, s) => n + s.items.length, 0)
                  : undefined
              }
              totalCount={
                isSearchActive || isBoardFilterActive
                  ? sections.reduce((n, s) => n + s.items.length, 0)
                  : undefined
              }
              data-testid={testIds.productBoard.roadmapPanelSearch}
            />
          </div>
        )}

        {roadmapDataReady && filtersExpanded && (
          <div
            className={`flex min-w-0 flex-col gap-2 ${isColumnsMode ? 'flex-shrink-0' : ''}`}
            data-testid={testIds.productBoard.roadmapBoardFilters}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant={boardFiltersState.mvpOnly ? 'default' : 'outline'}
                size="sm"
                className={
                  boardFiltersState.mvpOnly
                    ? 'h-8 border-amber-500/50 bg-amber-500/15 text-amber-900 hover:bg-amber-500/25 dark:text-amber-200'
                    : 'h-8'
                }
                onClick={toggleBoardFilterMvp}
                data-testid={testIds.productBoard.roadmapFilterMvpChip}
              >
                <Target className="mr-1 h-3.5 w-3.5 shrink-0" />
                MVP critical
                <span className="ml-1 text-muted-foreground tabular-nums">({boardFilterMvpCount})</span>
              </Button>
              <Button
                type="button"
                variant={boardFiltersState.dispatchOnly ? 'default' : 'outline'}
                size="sm"
                className="h-8"
                onClick={toggleBoardFilterDispatch}
                data-testid={testIds.productBoard.roadmapFilterDispatchChip}
              >
                Dispatchable now
                <span className="ml-1 text-muted-foreground tabular-nums">({boardFilterDispatchCount})</span>
              </Button>
              {inlineBoardTagFilters.map(({ tag, count }) => (
                <Button
                  key={tag}
                  type="button"
                  variant={boardFiltersState.tagConjunction.includes(tag) ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 gap-1 px-2 text-xs font-normal"
                  onClick={() => toggleBoardFilterTag(tag)}
                  data-testid={testIds.productBoard.roadmapFilterTag(tag)}
                >
                  <span className="truncate max-w-[12rem]">{tag}</span>
                  <span className="text-muted-foreground tabular-nums">({count})</span>
                </Button>
              ))}
              {overflowBoardTagFilters.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1 px-2 text-muted-foreground"
                      aria-label={`${overflowBoardTagFilters.length} more tag filters`}
                      data-testid={testIds.productBoard.roadmapFilterTagsMore}
                    >
                      <MoreHorizontal className="h-4 w-4 shrink-0" />
                      <span className="text-xs tabular-nums">+{overflowBoardTagFilters.length}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                    collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                  >
                    {overflowBoardTagFilters.map(({ tag, count }) => {
                      const active = boardFiltersState.tagConjunction.includes(tag);
                      return (
                        <DropdownMenuItem
                          key={tag}
                          className={`flex items-center justify-between gap-2 ${active ? 'bg-accent' : ''}`}
                          onClick={() => toggleBoardFilterTag(tag)}
                        >
                          <span className="truncate">{tag}</span>
                          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                            ({count})
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <select
                value={
                  boardFiltersState.cycle === 'all'
                    ? 'all'
                    : boardFiltersState.cycle === 'none'
                      ? 'none'
                      : String(boardFiltersState.cycle)
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'all') setBoardFilterCycle('all');
                  else if (v === 'none') setBoardFilterCycle('none');
                  else setBoardFilterCycle(Number(v));
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Filter by planning cycle"
                data-testid={testIds.productBoard.roadmapFilterCycle}
              >
                <option value="all">All cycles</option>
                {boardCycleSelectRows.map((row) => (
                  <option key={row.value} value={row.value}>
                    {row.label}
                  </option>
                ))}
                {boardFiltersState.cycle !== 'all' &&
                  boardFiltersState.cycle !== 'none' &&
                  !boardCycleSelectRows.some((r) => r.value === String(boardFiltersState.cycle)) && (
                    <option value={String(boardFiltersState.cycle)}>
                      Cycle {boardFiltersState.cycle} (0)
                    </option>
                  )}
              </select>
              {isBoardFilterActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-muted-foreground"
                  onClick={clearBoardFilters}
                  data-testid={testIds.productBoard.roadmapFilterClear}
                >
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        )}


        {/* Skeleton until first /api/roadmap response; then full DnD board */}
        {!roadmapDataReady ? (
          <RoadmapLoadingSkeleton mode={roadmapViewMode} />
        ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {roadmapViewMode === 'rows' ? (
            renderRowsView()
          ) : (
            /* Dynamic Columns Layout — flex-1 fills remaining height; sticky headers pin inside this scroll container */
            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
            <div className="flex flex-nowrap gap-4 pt-3 pb-8 sm:gap-6 min-w-max items-start">
            {displaySections.map((section, index) => {
              const colors = sectionColors[section.color];
              const isFirst = index === 0;
              const isLast = index === sections.length - 1;
              const isAnimating = animatingIds.has(section.id);
              const isEditingTitle = editingSectionId === section.id;

              return (
                <div
                  key={section.id}
                  className={`w-[300px] flex flex-col rounded-lg p-2 -m-2 transition-all duration-200 group/column ${colors.hoverBg} ${isAnimating ? 'ring-2 ring-primary ring-offset-2 animate-pulse' : ''}`}
                >
                  {/* Sticky column header — pins to top of the shared scroll container */}
                  <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm pb-2 rounded-t-lg"
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {isEditingTitle ? (
                          <input
                            type="text"
                            value={editingSectionTitle}
                            onChange={(e) => setEditingSectionTitle(e.target.value)}
                            onBlur={() => renameSection(section.id, editingSectionTitle)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') renameSection(section.id, editingSectionTitle);
                              if (e.key === 'Escape') { setEditingSectionId(null); setEditingSectionTitle(''); }
                            }}
                            className="text-lg font-semibold bg-transparent border-b border-primary outline-none px-1 min-w-0 flex-1"
                            autoFocus
                          />
                        ) : (
                          <h2 className="text-lg font-semibold shrink-0">{section.title}</h2>
                        )}
                        {section.badge && (
                          <span className={`px-2 py-0.5 text-xs rounded shrink-0 ${colors.bg} ${colors.text} border ${colors.border}`}>
                            {section.badge}
                          </span>
                        )}
                      </div>

                      {/* ... context menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 opacity-0 group-hover/column:opacity-100 transition-opacity"
                            data-testid={testIds.productBoard.roadmapColumnOpenPreview(section.id)}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                      align="end"
                      className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                      collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                    >
                          <DropdownMenuItem
                            onClick={() => { setEditingSectionId(section.id); setEditingSectionTitle(section.title); }}
                            className="gap-2"
                          >
                            <Pencil className="w-4 h-4" /> Rename
                          </DropdownMenuItem>

                          {/* Color palette */}
                          <div className="px-2 py-1.5">
                            <p className="text-xs text-muted-foreground mb-1.5">Color</p>
                            <div className="flex gap-1.5">
                              {ALL_SECTION_COLORS.map(c => (
                                <button
                                  key={c}
                                  onClick={() => changeColumnColor(section.id, c)}
                                  className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${sectionColors[c].dotBg} ${section.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                                  title={c}
                                />
                              ))}
                            </div>
                          </div>

                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => moveSectionLeft(section.id)} disabled={isFirst} className="gap-2">
                            <MoveLeft className="w-4 h-4" /> Move left
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => moveSectionRight(section.id)} disabled={isLast} className="gap-2">
                            <MoveRight className="w-4 h-4" /> Move right
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {section.sourceFilePath && (
                            <DropdownMenuItem onClick={() => handleOpenRoadmapColumnDocInPanel(section.id)} className="gap-2">
                              <PanelRight className="w-4 h-4" /> Open source doc
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => setAddItemSectionId(addItemSectionId === section.id ? null : section.id)}
                            className="gap-2"
                          >
                            <Plus className="w-4 h-4" /> Add item
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => deleteSection(section.id)}
                            disabled={section.items.length > 0}
                            className={cn('gap-2', QUIET_MENU_DESTRUCTIVE_ITEM_CLASS)}
                          >
                            <Trash2 className="w-4 h-4" /> Delete column
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {section.items.length} item{section.items.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  {/* Scrollable column body */}
                  <div className="space-y-2 pt-1">
                    {/* Add item search dropdown */}
                    {addItemSectionId === section.id && (
                      <div className="bg-card border border-border rounded-lg shadow-lg z-50">
                        <div className="p-3 border-b">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Search documents..."
                              value={addItemSearch}
                              onChange={(e) => setAddItemSearch(e.target.value)}
                              className="pl-8"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredSearchResults.map((result) => (
                            <button
                              key={result.id}
                              onClick={() => addItemToSection(section.id, result)}
                              className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm"
                            >
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <span>{result.title}</span>
                              <span className="text-xs text-muted-foreground ml-auto">{result.type}</span>
                            </button>
                          ))}
                        </div>
                        <div className="border-t p-2 space-y-1">
                          <button onClick={() => createNewItem(section.id, 'bet')} className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm rounded">
                            <Plus className="w-4 h-4 text-green-500" /><span>Create new bet</span>
                          </button>
                          <button onClick={() => createNewItem(section.id, 'note')} className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm rounded">
                            <Plus className="w-4 h-4 text-blue-500" /><span>Create new note</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Column items */}
                    <SortableContext items={section.items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                      <DroppableSection
                        section={section}
                        isOver={overId === `section-drop-${section.id}` || section.items.some(i => i.id === overId)}
                      >
                        {section.items.length === 0 ? (
                          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">
                            No items in this column
                          </div>
                        ) : (
                          section.items.map((item) => (
                            <SortableBetCard
                              key={item.id}
                              item={item}
                              sectionId={section.id}
                              isAnimating={animatingIds.has(item.id)}
                              onOpenInPanel={handleOpenBetInPanel}
                              otherSections={sections.filter(s => s.id !== section.id)}
                              onMoveToSection={moveItemToSection}
                              onArchive={archiveItem}
                              isInDoneColumn={section.id === 'done'}
                              delegateMcpOrganizationId={delegateMcpOrganizationId}
                              searchDimmed={betSearchDim(item)}
                              isActive={sidePanelBetId === item.id}
                              onDispatchToCloud={handleDispatchToCloud}
                              onPromoteFeedback={(ticket) => {
                                const path = window.prompt(
                                  'promoted_to path (incident/bet file path):',
                                  ''
                                );
                                if (path) void promoteFeedbackTicket(ticket, path);
                              }}
                              isDispatching={cloudDispatchingId === item.id}
                              isDispatched={
                              isActiveCloudDispatch(cloudDispatchResult[item.id]) ||
                              Boolean(item.dispatchedAgentId?.trim() && item.dispatchStatus === 'running')
                            }
                            />
                          ))
                        )}
                      </DroppableSection>
                    </SortableContext>

                    {/* Add item placeholder */}
                    <button
                      onClick={() => setAddItemSectionId(addItemSectionId === section.id ? null : section.id)}
                      className="w-full py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-dashed border-border rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add item...
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Add Column button */}
            <div className="flex-shrink-0 w-[200px] flex items-start pt-10">
              <button
                onClick={addSection}
                className="w-full py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-dashed border-border rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Column
              </button>
            </div>
            </div>
          </div>
          )}
          
          {/* Drag Overlay - shows floating preview of dragged item */}
          <DragOverlay>
            {activeDragItem && (
              <div className="border-l-4 border-l-primary border-r border-t border-b border-border rounded-lg p-4 bg-card shadow-xl opacity-90 w-[300px]">
                <div className="flex items-center gap-2 mb-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-medium text-sm">{activeDragItem.title}</h3>
                </div>
                {activeDragItem.appetite && (
                  <span className="px-2 py-0.5 text-xs rounded bg-purple-500/10 text-purple-500 border border-purple-500/20">
                    {activeDragItem.appetite}
                  </span>
                )}
              </div>
            )}
          </DragOverlay>
        </DndContext>
        )}

        {/* Legend */}
        <div className="border border-border rounded-lg p-4 bg-muted/30">
          <div className="flex items-center gap-8 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border-l-4 border-l-red-500"></div>
              <span className="text-muted-foreground">High Priority</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border-l-4 border-l-yellow-500"></div>
              <span className="text-muted-foreground">Medium Priority</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 border-l-4 border-l-gray-500"></div>
              <span className="text-muted-foreground">Low Priority</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStatusBoard = () => {
    const nowSection = sections.find((s) => s.id === 'now');
    const nowItems = nowSection?.items ?? [];
    const today = new Date().toISOString().slice(0, 10);
    const checklistKeys = [
      'update-last-board',
      'set-last-worked-next-steps',
      'move-completed-to-done',
      'add-blocked',
      'session-note',
    ];

    return (
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Pick up where you left off. Last worked, next steps, blocked. Use at end of session or handoff.
        </p>

        {/* Last board update */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-muted-foreground">Last board update:</span>
          <span className="font-medium">{lastBoardUpdate}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setLastBoardUpdate(today);
              toast.success('Last board update set to today');
            }}
          >
            <Calendar className="w-4 h-4 mr-1" />
            Update to today
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={roadmapLoading}
            onClick={() => { loadRoadmapData(); toast.info('Refreshing status board…'); }}
            title="Reload data from roadmap files"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${roadmapLoading ? 'animate-spin' : ''}`} />
            {roadmapLoading ? 'Loading…' : 'Refresh'}
          </Button>
        </div>

        {/* Active NOW table */}
        <div className="border border-border rounded-lg overflow-hidden bg-card">
          <div className="px-4 py-3 border-b bg-muted/30 font-semibold text-sm flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
            Active NOW
            <span className="text-muted-foreground font-normal">({nowItems.length} items · from Roadmap)</span>
          </div>
          <div className="divide-y divide-border">
            {nowItems.length === 0 ? (
              <div className="p-6 text-muted-foreground text-center text-sm">
                No items in NOW. Add items in the Roadmap view.
              </div>
            ) : (
              nowItems.map((item) => {
                // Staleness: days since last worked
                const daysSince = item.lastWorked
                  ? Math.floor((Date.now() - new Date(item.lastWorked).getTime()) / 86400000)
                  : null;
                const stalenessColor =
                  daysSince === null ? 'text-red-400' :
                  daysSince > 14 ? 'text-red-400' :
                  daysSince > 7 ? 'text-yellow-400' :
                  'text-green-400';
                const stalenessLabel =
                  daysSince === null ? 'never' :
                  daysSince === 0 ? 'today' :
                  daysSince === 1 ? '1d ago' :
                  `${daysSince}d ago`;

                // Type badge
                const typeBadge = !item.type ? 'bug' :
                  item.appetite?.toLowerCase().includes('half') ? 'quick-win' :
                  (item.progress ?? 0) > 30 && item.mustHaveTotal === undefined ? 'carryover' :
                  item.type;

                const badgeColors: Record<string, string> = {
                  'big': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
                  'medium': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
                  'small': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
                  'quick-win': 'bg-green-500/10 text-green-400 border-green-500/20',
                  'carryover': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
                  'bug': 'bg-red-500/10 text-red-400 border-red-500/20',
                };

                // Truncate title for display (keep first ~55 chars up to a word boundary)
                const shortTitle = item.title.length > 56
                  ? item.title.slice(0, 56).replace(/\s\S*$/, '') + '…'
                  : item.title;

                const progressValue = item.progress ?? 0;
                const progressColor =
                  progressValue >= 80 ? 'bg-green-500' :
                  progressValue >= 40 ? 'bg-blue-500' :
                  progressValue > 0 ? 'bg-yellow-500' :
                  'bg-muted-foreground/30';

                return (
                  <div key={item.id} className="group p-3 hover:bg-muted/20 grid gap-x-3 gap-y-0 items-center" style={{ gridTemplateColumns: '1fr 90px 112px 1fr 28px' }}>
                    {/* Col 1: Title + badges + must-haves */}
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="text-left font-semibold text-foreground hover:text-primary hover:underline text-sm leading-snug"
                        title={item.title}
                        onClick={() => {
                          setSidePanelBetId(item.id);
                          setSidePanelView('bet-preview');
                        }}
                      >
                        {shortTitle}
                      </button>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${badgeColors[typeBadge] ?? badgeColors['small']}`}>
                          {typeBadge}
                        </span>
                        {item.mustHaveTotal !== undefined && (
                          <span className="text-[10px] text-muted-foreground">
                            {item.mustHaveDone ?? 0}/{item.mustHaveTotal} must-haves
                          </span>
                        )}
                        {item.appetite && (
                          <span className="text-[10px] text-muted-foreground">{item.appetite}</span>
                        )}
                      </div>
                    </div>

                    {/* Col 2: Progress bar */}
                    <div className="flex flex-col justify-center gap-1 px-1">
                      <span className="text-xs text-muted-foreground">{item.progress !== undefined ? `${item.progress}%` : '—'}</span>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden w-full">
                        <div
                          className={`h-full rounded-full transition-all ${progressColor}`}
                          style={{ width: `${progressValue}%` }}
                        />
                      </div>
                    </div>

                    {/* Col 3: Last worked */}
                    <div className="flex flex-col gap-1 justify-center">
                      <div className="flex items-center gap-1">
                        <Clock className={`w-3 h-3 flex-shrink-0 ${stalenessColor}`} />
                        <span className={`text-[11px] font-medium ${stalenessColor} flex-shrink-0`}>{stalenessLabel}</span>
                      </div>
                      <Input
                        className="h-6 text-[11px] px-1.5 w-full"
                        placeholder="YYYY-MM-DD"
                        value={item.lastWorked ?? ''}
                        onChange={(e) => updateItemStatusBoard(item.id, e.target.value, item.nextSteps ?? '')}
                      />
                    </div>

                    {/* Col 4: Next steps */}
                    <div className="flex items-center min-w-0">
                      <Input
                        className="h-7 text-[11px] px-2 w-full"
                        placeholder="Next steps…"
                        title={item.nextSteps ?? ''}
                        value={item.nextSteps ?? ''}
                        onChange={(e) => updateItemStatusBoard(item.id, item.lastWorked ?? '', e.target.value)}
                      />
                    </div>

                    {/* Col 5: Actions menu */}
                    <div className="flex items-center justify-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title="Actions"
                          >
                            <MoreHorizontal className="w-3.5 h-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                          collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                        >
                          {item.path && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  const name = getBetFileName(item.path);
                                  if (name) { navigator.clipboard.writeText(name); toast.success('Filename copied', { description: name }); }
                                }}
                                className="gap-2"
                              >
                                <Copy className="w-4 h-4" />
                                Copy filename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  const p = getRawBetFilePath(item.path);
                                  if (p) { navigator.clipboard.writeText(p); toast.success('Path copied', { description: p }); }
                                }}
                                className="gap-2"
                              >
                                <Copy className="w-4 h-4" />
                                Copy path
                              </DropdownMenuItem>
                              {normalizeBetDocFilePath(item.path) && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    const s = buildMcpPathSnippet(item.path, delegateMcpOrganizationId);
                                    if (s) {
                                      navigator.clipboard.writeText(s);
                                      toast.success('MCP lookup copied', { description: s });
                                    }
                                  }}
                                  className="gap-2"
                                >
                                  <Copy className="w-4 h-4" />
                                  Copy MCP lookup
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => {
                                  navigator.clipboard.writeText(buildDelegatePrompt(item, delegateMcpOrganizationId));
                                  toast.success('Delegate prompt copied');
                                }}
                                className="gap-2"
                              >
                                <Share2 className="w-4 h-4" />
                                Copy delegate prompt
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  const docPath = normalizeBetDocFilePath(item.path)!;
                                  window.dispatchEvent(new CustomEvent('add-to-chat', {
                                    detail: { type: 'document', id: betDocumentReferenceId(item), title: item.title, name: item.title, documentType: 'bet', path: docPath }
                                  }));
                                  toast.success('Added to chat', { description: item.title });
                                }}
                                className="gap-2"
                              >
                                <MessageSquarePlus className="w-4 h-4" />
                                Add to chat
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* MR Queue */}
        {(() => {
          const mrItems = sections
            .flatMap((s) => s.items)
            .filter((item) => !!item.gitMrUrl);
          if (mrItems.length === 0) return null;

          const statusLabel = (item: (typeof mrItems)[0]) => {
            const s = item.status ?? '';
            if (s === 'complete') return { label: 'Merged', cls: 'bg-green-500/10 text-green-400 border-green-500/20' };
            if (s === 'in_progress') return { label: 'Open MR', cls: 'bg-purple-500/10 text-purple-400 border-purple-500/20' };
            return { label: s || 'Open MR', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' };
          };

          return (
            <div className="border border-border rounded-lg overflow-hidden bg-card">
              <div className="px-4 py-3 border-b bg-purple-500/5 font-semibold text-sm flex items-center gap-2">
                <span className="text-base">🔀</span>
                MR Queue
                <span className="text-muted-foreground font-normal">({mrItems.length} open)</span>
              </div>
              <div className="divide-y divide-border">
                {mrItems.map((item) => {
                  const { label, cls } = statusLabel(item);
                  return (
                    <div key={item.id} className="group flex items-center gap-3 px-4 py-2.5 hover:bg-muted/20">
                      <button
                        type="button"
                        className="flex-1 min-w-0 text-left text-sm font-medium text-foreground hover:text-primary hover:underline truncate"
                        title={item.title}
                        onClick={() => { setSidePanelBetId(item.id); setSidePanelView('bet-preview'); }}
                      >
                        {item.title.length > 60 ? item.title.slice(0, 60).replace(/\s\S*$/, '') + '…' : item.title}
                      </button>
                      {item.workingBranch && (
                        <code className="hidden sm:block text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground max-w-[180px] truncate flex-shrink-0" title={item.workingBranch}>
                          {item.workingBranch}
                        </code>
                      )}
                      <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${cls}`}>
                        {label}
                      </span>
                      <a
                        href={item.gitMrUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
                        title={item.gitMrUrl}
                      >
                        <ExternalLink className="w-3 h-3" />
                        View MR
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Blocked / Needs decision */}
        <div className="border border-border rounded-lg overflow-hidden bg-card">
          <div className="px-4 py-3 border-b bg-yellow-500/10 font-semibold flex items-center justify-between">
            <span>🟡 Blocked / Needs decision</span>
            <Button variant="outline" size="sm" onClick={addBlockedItem}>
              <Plus className="w-4 h-4 mr-1" />
              Add row
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/20">
                  <th className="text-left p-3 font-medium">Item</th>
                  <th className="text-left p-3 font-medium">Blocked on</th>
                  <th className="text-left p-3 font-medium">Owner</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {blockedItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-4 text-muted-foreground text-center">
                      (none)
                    </td>
                  </tr>
                ) : (
                  blockedItems.map((b) => (
                    <tr key={b.id} className="border-b last:border-b-0">
                      <td className="p-2">
                        <Input
                          className="h-8 text-xs"
                          placeholder="Item"
                          value={b.item}
                          onChange={(e) => updateBlockedItem(b.id, 'item', e.target.value)}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          className="h-8 text-xs"
                          placeholder="Blocked on"
                          value={b.blockedOn}
                          onChange={(e) => updateBlockedItem(b.id, 'blockedOn', e.target.value)}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          className="h-8 text-xs"
                          placeholder="Owner"
                          value={b.owner}
                          onChange={(e) => updateBlockedItem(b.id, 'owner', e.target.value)}
                        />
                      </td>
                      <td className="p-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeBlockedItem(b.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* End-of-session checklist */}
        <div className="border border-border rounded-lg p-4 bg-card">
          <h3 className="font-semibold mb-3">📋 End-of-session checklist (when you stop for the day)</h3>
          <ul className="space-y-2 text-sm">
            {[
              'Update **Last board update** at top (today\'s date).',
              'For any bet you touched: set **Last worked** = today, **Next steps** = 1–3 concrete actions.',
              'Move completed items to "Completed this cycle" in NOW and tick their checkboxes in the bet doc.',
              'If something is blocked, add it under **Blocked / Needs decision**.',
              'Optional: append a 2–3 line note under **Session notes**.',
            ].map((label, idx) => {
              const key = checklistKeys[idx];
              const checked = endOfSessionChecks[key] ?? false;
              return (
                <li key={key} className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => setEndOfSessionChecks((p) => ({ ...p, [key]: !p[key] }))}
                    className="shrink-0 mt-0.5"
                  >
                    {checked ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <Circle className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                  <span className={checked ? 'text-muted-foreground line-through' : ''}>{label.replace(/\*\*/g, '')}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Session notes */}
        <div className="border border-border rounded-lg p-4 bg-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Session notes (optional one-liners)</h3>
            {!showNoteInput && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowNoteInput(true);
                  setNoteInputText('');
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add note
              </Button>
            )}
          </div>

          {showNoteInput && (
            <div className="flex items-center gap-2 mb-3">
              <Input
                autoFocus
                placeholder="Brief session note — what happened today?"
                value={noteInputText}
                onChange={(e) => setNoteInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && noteInputText.trim()) {
                    addSessionNote(noteInputText.trim());
                    setNoteInputText('');
                    setShowNoteInput(false);
                  }
                  if (e.key === 'Escape') {
                    setNoteInputText('');
                    setShowNoteInput(false);
                  }
                }}
                className="text-sm"
              />
              <Button
                size="sm"
                disabled={!noteInputText.trim()}
                onClick={() => {
                  if (noteInputText.trim()) {
                    addSessionNote(noteInputText.trim());
                    setNoteInputText('');
                    setShowNoteInput(false);
                  }
                }}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setNoteInputText('');
                  setShowNoteInput(false);
                }}
              >
                Cancel
              </Button>
            </div>
          )}

          <ul className="space-y-1 text-sm text-muted-foreground">
            {sessionNotes.map((n, i) => (
              <li key={i}>
                <span className="font-medium text-foreground">{n.date}:</span> {n.text}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-muted-foreground">
          How this fits: NOW = what we&apos;re doing. Status Board = where we are and what&apos;s next. Bet docs = scope and history. Product Board UI (this) = same info in the webapp.
        </p>
      </div>
    );
  };

  const renderCycleDetail = () => (
    <ProductBoardCycleDetail
      selectedBetId={selectedBetId}
      completedTasks={completedTasks}
      handleTaskToggle={handleTaskToggle}
      sidePanelView={sidePanelView}
      handleOpenInEditor={handleOpenInEditor}
      handleCloseSidePanel={handleCloseSidePanel}
    />
  );

  const renderSidePanel = () => {
    if (!sidePanelView) return null;

    if (sidePanelView === 'bet-preview' && !sidePanelBetId) {
      return (
        <div className="h-full flex flex-col bg-card" data-testid={testIds.productBoard.betPreviewEmpty}>
          <PreviewPaneHeader
            row
            icon={<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
            label="Bet Preview"
            onClose={handleCloseSidePanel}
          />
          <div className="flex flex-1 flex-col items-start justify-center gap-2 px-6 py-8 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">No bet selected</p>
            <p>Click a roadmap card to preview it here.</p>
          </div>
        </div>
      );
    }

    if (sidePanelView === 'bet-preview' && sidePanelBetId) {
      const previewItem = sections.flatMap((s) => s.items).find((i) => i.id === sidePanelBetId);
      const canOpenBet = Boolean(previewItem && normalizeBetDocFilePath(previewItem.path));
      const previewBody =
        betPreviewError != null
          ? `# Preview unavailable\n\n${betPreviewError}${
              canOpenBet
                ? '\n\nTry **Open** to load the bet in the editor if the file exists.'
                : '\n\nThis item has no linked bet file path yet.'
            }`
          : (betPreviewMarkdown ?? '');

      const previewSection = sections.find((s) => s.items.some((i) => i.id === sidePanelBetId));
      const previewSectionColors = previewSection ? sectionColors[previewSection.color] : null;

      const betDropdownContent = previewItem && normalizeBetDocFilePath(previewItem.path) ? (
        <>
          <DropdownMenuItem
            onClick={() => {
              const n = getBetFileName(previewItem.path);
              if (n) { navigator.clipboard.writeText(n); toast.success('Filename copied', { description: n }); }
            }}
            className="gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy filename
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const p = getRawBetFilePath(previewItem.path);
              if (p) { navigator.clipboard.writeText(p); toast.success('Path copied', { description: p }); }
            }}
            className="gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy path
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const s = buildMcpPathSnippet(previewItem.path, delegateMcpOrganizationId);
              if (s) { navigator.clipboard.writeText(s); toast.success('MCP lookup copied', { description: s }); }
            }}
            className="gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy MCP lookup
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigator.clipboard.writeText(buildDelegatePrompt(previewItem, delegateMcpOrganizationId));
              toast.success('Delegate prompt copied');
            }}
            className="gap-2"
          >
            <Share2 className="w-4 h-4" />
            Copy delegate prompt
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              const docPath = normalizeBetDocFilePath(previewItem.path)!;
              if (!previewItem.documentId) {
                toast.warning('Bet not indexed yet', { description: 'Chat will resolve by file path if possible.' });
              }
              window.dispatchEvent(new CustomEvent('add-to-chat', {
                detail: { type: 'document', id: betDocumentReferenceId(previewItem), title: previewItem.title, name: previewItem.title, documentType: 'bet', path: docPath },
              }));
              toast.success('Added to chat', { description: previewItem.title });
            }}
            className="gap-2"
          >
            <MessageSquarePlus className="w-4 h-4" />
            Add to chat
          </DropdownMenuItem>
          {isDispatchReady(previewItem) &&
            !isActiveCloudDispatch(cloudDispatchResult[previewItem.id]) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDispatchToCloud(previewItem)}
                disabled={cloudDispatchingId === previewItem.id}
                className="gap-2"
              >
                {cloudDispatchingId === previewItem.id
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <span className="text-sm leading-none">⚡</span>
                }
                Dispatch to Cloud Agent
              </DropdownMenuItem>
            </>
          )}
        </>
      ) : (
        <DropdownMenuItem disabled>No file path for this preview</DropdownMenuItem>
      );

      const betChips = previewSection && previewSectionColors ? (
        <button
          onClick={() => {
            const el = document.querySelector<HTMLElement>(`[data-bet-id="${sidePanelBetId}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }}
          title={`Scroll to card in ${previewSection.title} column`}
          className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase tracking-wider leading-none flex-shrink-0 transition-opacity hover:opacity-70 cursor-pointer ${previewSectionColors.bg} ${previewSectionColors.text} ${previewSectionColors.border}`}
        >
          {previewSection.title}
        </button>
      ) : undefined;

      return (
        <div className="h-full flex flex-col bg-card">
          <PreviewPaneHeader
            row
            icon={<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
            label="Bet Preview"
            chips={betChips}
            dropdownContent={betDropdownContent}
            dropdownTestId={testIds.productBoard.betPreviewActions}
            onOpen={handleOpenInEditor}
            openDisabled={!canOpenBet}
            openTitle="Open in tab"
            openTestId={testIds.productBoard.betPreviewOpen}
            onClose={handleCloseSidePanel}
          />

          {previewItem ? (
            <BetPreviewCloudDispatchSection
              item={previewItem}
              organizationId={delegateMcpOrganizationId}
              onWatchLive={setWatchLiveAgentId}
              onDispatchSync={syncCloudDispatchForItem}
              sessionDispatch={cloudDispatchResult[previewItem.id] ?? null}
              betPreviewMarkdown={betPreviewMarkdown}
              fetchEnabled={Boolean(
                (resolveCloudDispatchTargets(previewItem, betPreviewMarkdown).dispatchPath ||
                  previewItem.documentId?.trim()) &&
                  (isDispatchReady(previewItem) ||
                    Boolean(previewItem.dispatchedAgentId?.trim()) ||
                    isActiveCloudDispatch(cloudDispatchResult[previewItem.id]))
              )}
            />
          ) : null}

          {/* Content: no outer padding — MarkdownPreview uses panel padding to avoid double gutters */}
          <div className="flex-1 overflow-y-auto p-0">
            {betPreviewLoading ? (
              <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading bet…
              </div>
            ) : (
              <ProductBoardMarkdownPreview
                content={previewBody}
                className="max-w-none"
                readingColumnPadding="panel"
                linkBasePath={
                  previewItem != null
                    ? (normalizeBetDocFilePath(previewItem.path) ??
                        documentFilePath)
                    : documentFilePath
                }
              />
            )}
          </div>
        </div>
      );
    }

    if (sidePanelView === 'roadmap-doc-preview' && sidePanelRoadmapPath) {
      const previewBody =
        betPreviewError != null
          ? `# Preview unavailable\n\n${betPreviewError}\n\nTry **Open** to load the file in the editor.`
          : (betPreviewMarkdown ?? '');
      const humanPath = sidePanelRoadmapPath.replace(/^flownotes-app-docs\//, 'docs/');
      const fileLabel = sidePanelRoadmapPath.split('/').pop() ?? 'Roadmap';

      const roadmapDropdownContent = (
        <>
          <DropdownMenuItem
            onClick={() => {
              navigator.clipboard.writeText(humanPath);
              toast.success('Path copied', { description: humanPath });
            }}
            className="gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy path (repo-relative)
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigator.clipboard.writeText(sidePanelRoadmapPath);
              toast.success('DB path copied', { description: sidePanelRoadmapPath });
            }}
            className="gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy DB file_path
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const s = buildMcpPathSnippet(sidePanelRoadmapPath, delegateMcpOrganizationId);
              if (s) {
                navigator.clipboard.writeText(s);
                toast.success('MCP lookup copied', { description: s });
              }
            }}
            className="gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy MCP lookup
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              window.dispatchEvent(new CustomEvent('add-to-chat', {
                detail: {
                  type: 'document',
                  id: sidePanelRoadmapPath,
                  title: fileLabel,
                  name: fileLabel,
                  documentType: 'roadmap',
                  path: sidePanelRoadmapPath,
                },
              }));
              toast.success('Added to chat', { description: fileLabel });
            }}
            className="gap-2"
          >
            <MessageSquarePlus className="w-4 h-4" />
            Add to chat
          </DropdownMenuItem>
        </>
      );

      return (
        <div className="h-full flex flex-col bg-card">
          <PreviewPaneHeader
            row
            icon={<FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
            label={`Roadmap · ${fileLabel}`}
            dropdownContent={roadmapDropdownContent}
            dropdownTestId={testIds.productBoard.roadmapDocPreviewActions}
            onOpen={handleOpenInEditor}
            onClose={handleCloseSidePanel}
          />

          <div className="flex-1 overflow-y-auto p-0">
            {betPreviewLoading ? (
              <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading roadmap file…
              </div>
            ) : (
              <ProductBoardMarkdownPreview
                content={previewBody}
                className="max-w-none"
                readingColumnPadding="panel"
                linkBasePath={sidePanelRoadmapPath}
              />
            )}
          </div>
        </div>
      );
    }

    if (sidePanelView === 'cycle-detail') {
      return renderCycleDetail();
    }

    return null;
  };

  return (
    <>
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-background" data-testid={testIds.productBoard.root}>
      <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-2">
        <div className="flex items-center gap-0.5 bg-muted/60 rounded-lg p-0.5">
          <button
            type="button"
            className={VIEW_TAB_CLASS(currentView === 'dashboard')}
            data-testid={testIds.productBoard.tabDashboard}
            onClick={() => setCurrentView('dashboard')}
          >
            <Home className="w-4 h-4" />
            Dashboard
          </button>
          <button
            type="button"
            className={VIEW_TAB_CLASS(currentView === 'roadmap')}
            data-testid={testIds.productBoard.tabRoadmap}
            onClick={() => setCurrentView('roadmap')}
          >
            <Map className="w-4 h-4" />
            Roadmap
          </button>
          <button
            type="button"
            className={VIEW_TAB_CLASS(currentView === 'status-board' || currentView === 'cycle-detail')}
            data-testid={testIds.productBoard.tabStatusBoard}
            onClick={() => setCurrentView('status-board')}
          >
            <ClipboardList className="w-4 h-4" />
            Status Board
          </button>
        </div>
        <PreviewPanelToggle
          open={Boolean(sidePanelView)}
          onToggle={handleToggleSidePanel}
          testId={testIds.productBoard.previewToggle}
        />
      </div>

      {/* Main Content with Side Panel */}
      <div className="flex-1 overflow-hidden">
        {(() => {
          // Roadmap-columns view uses a flex-fill layout (no outer scroll, no maxHeight hack).
          // All other views (dashboard, rows, status board) use normal overflow-auto scrolling.
          const isRoadmapColumns = currentView === 'roadmap' && roadmapViewMode === 'columns';
          const panelContentClass = isRoadmapColumns
            ? "h-full min-w-0 overflow-hidden px-4 sm:px-6"
            : "h-full min-w-0 overflow-auto px-4 py-6 sm:px-6";
          const wrapperClass = isRoadmapColumns
            ? "h-full min-w-0 max-w-none"
            : "w-full min-w-0 max-w-none";

          const mainContent = (
            <div className={panelContentClass}>
              <div className={wrapperClass}>
                {currentView === 'dashboard' && renderDashboard()}
                {currentView === 'roadmap' && renderRoadmap()}
                {currentView === 'status-board' && renderStatusBoard()}
                {currentView === 'cycle-detail' && renderCycleDetail()}
              </div>
            </div>
          );

          return sidePanelView ? (
            <PanelGroup direction="horizontal" className="h-full min-w-0">
              {/* Main Content Panel */}
              <Panel defaultSize={60} minSize={40} className="min-w-0">
                {mainContent}
              </Panel>

              {/* Resize Handle */}
              <PanelResizeHandle className="w-px bg-border hover:bg-primary transition-colors" />

              {/* Side Panel */}
              <Panel defaultSize={40} minSize={30} maxSize={60} className="min-w-0">
                {renderSidePanel()}
              </Panel>
            </PanelGroup>
          ) : mainContent;
        })()}
      </div>

      {chrome === 'page' ? (
        <ListSurfaceStatusBar
          tips={LIST_GESTURE_TIPS_PRODUCT_BOARD}
          includePreviewTip
          data-testid={testIds.productBoard.statusBar}
        />
      ) : null}
      </div>
      <Dialog
        open={!!watchLiveAgentId}
        onOpenChange={(open) => {
          if (!open) setWatchLiveAgentId(null);
        }}
      >
        <DialogContent
          className="max-w-2xl"
          data-testid={testIds.inbox.agentWatchLiveDialog}
        >
          <DialogHeader>
            <DialogTitle>Cloud agent live status</DialogTitle>
          </DialogHeader>
          {watchLiveAgentId ? (
            <AgentLiveStreamPanel
              agentId={watchLiveAgentId}
              organizationId={delegateMcpOrganizationId}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      <Toaster />
    </>
  );
}


